import { useState } from 'react';
import { Menu, User, LogOut, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditsProgressBar } from '@/components/CreditsProgressBar';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import SystemStatusIndicator from '@/components/SystemStatusIndicator';
import { UserMenu } from '@/components/UserMenu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, loading, signOut } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auto-logout after 4 hours of inactivity (extended for voice/consultation sessions)
  useAutoLogout();

  const scrollToSection = (sectionId: string) => {
    // Only scroll if we're on the homepage
    if (window.location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage and then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setIsOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setTimeout(() => window.scrollTo(0, 0), 100);
    setIsOpen(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setIsOpen(false);
  };

  // Função para verificar se a rota está ativa
  const isActiveRoute = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Classe CSS para item ativo
  const getNavLinkClass = (path: string) => {
    const baseClass = "relative text-foreground hover:text-primary transition-colors font-medium after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:transition-transform after:duration-300";
    const activeClass = isActiveRoute(path) 
      ? "text-primary after:scale-x-100" 
      : "after:scale-x-0 after:origin-bottom-right hover:after:scale-x-100 hover:after:origin-bottom-left";
    return `${baseClass} ${activeClass}`;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border shadow-sm ios-safe-top">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button 
            onClick={() => handleNavigate('/')} 
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity touch-manipulation min-h-[44px] min-w-[44px]"
          >
            <img 
              src="/lovable-uploads/a90037e4-0e6f-4f0a-85cd-f03382deeb41.png" 
              alt="AI4ME - logomarca"
              className="h-8 max-w-[140px] object-contain"
              style={{ aspectRatio: 'auto' }}
              loading="eager"
              decoding="async"
            />
          </button>

          {/* Desktop Menu - Order: INÍCIO, PLANOS, ConsultorIA, CURSOS, PROMPTS, BLOG */}
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('inicio')} title="Ir para página inicial" className={getNavLinkClass('/')}>
              Início
            </button>
            <button onClick={() => handleNavigate('/planos')} title="Escolha seu plano de assinatura ideal" className={getNavLinkClass('/planos')}>
              Planos
            </button>
            <button onClick={() => handleNavigate('/ai4me')} title="Acesse a Consultoria Inteligente AI4ME" className={getNavLinkClass('/ai4me')}>
              ConsultorIA
            </button>
            <button onClick={() => handleNavigate('/curso-ia')} title="Curso de IA para empreendedores" className={getNavLinkClass('/curso-ia')}>
              Cursos
            </button>
            <button onClick={() => handleNavigate('/prompts')} title="Conheça os prompts da AI4ME" className={getNavLinkClass('/prompts')}>
              Prompts
            </button>
            <button onClick={() => handleNavigate('/blog')} title="Leia artigos sobre IA e tecnologia" className={getNavLinkClass('/blog')}>
              Blog
            </button>
            {user && (
              <button onClick={() => handleNavigate('/meu-pin')} title="Acesse seu Plano Inteligente de Negócios" className={getNavLinkClass('/meu-pin')}>
                Meu PIN
              </button>
            )}
            {/* Show auth buttons only after loading completes */}
            {!loading && !adminLoading && (
              <>
                {user ? (
                  <div className="flex items-center space-x-3">
                    {/* Indicador de status do sistema */}
                    <SystemStatusIndicator compact />
                    
                    {/* Não mostrar créditos para admins */}
                    {!isAdmin && <CreditsProgressBar compact showBuyButton={false} />}
                    
                    {/* Menu do usuário com dropdown */}
                    <UserMenu 
                      onNavigate={handleNavigate} 
                      onLogout={handleLogout} 
                    />
                  </div>
                ) : (
                  <Button 
                    onClick={() => handleNavigate('/auth')}
                    size="sm"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Entrar
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Mobile Menu Button - iOS optimized touch target */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-3 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation active:bg-muted/50 rounded-lg transition-colors"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu - iOS optimized with safe areas and touch targets */}
        {isOpen && (
          <div 
            id="mobile-menu" 
            role="menu" 
            className="md:hidden py-4 border-t border-border animate-fade-in max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
          >
            <div className="flex flex-col space-y-1">
              <button 
                onClick={() => scrollToSection('inicio')} 
                className={`${getNavLinkClass('/')} text-left py-3 px-2 min-h-[44px] touch-manipulation active:bg-muted/50 rounded-lg transition-colors`}
              >
                Início
              </button>
              <button 
                onClick={() => handleNavigate('/planos')} 
                className={`${getNavLinkClass('/planos')} text-left py-3 px-2 min-h-[44px] touch-manipulation active:bg-muted/50 rounded-lg transition-colors`}
              >
                Planos
              </button>
              <button 
                onClick={() => handleNavigate('/ai4me')} 
                className={`${getNavLinkClass('/ai4me')} text-left py-3 px-2 min-h-[44px] touch-manipulation active:bg-muted/50 rounded-lg transition-colors`}
              >
                ConsultorIA
              </button>
              <button 
                onClick={() => handleNavigate('/curso-ia')} 
                className={`${getNavLinkClass('/curso-ia')} text-left py-3 px-2 min-h-[44px] touch-manipulation active:bg-muted/50 rounded-lg transition-colors`}
              >
                Cursos
              </button>
              <button 
                onClick={() => handleNavigate('/prompts')} 
                className={`${getNavLinkClass('/prompts')} text-left py-3 px-2 min-h-[44px] touch-manipulation active:bg-muted/50 rounded-lg transition-colors`}
              >
                Prompts
              </button>
              <button 
                onClick={() => handleNavigate('/blog')} 
                className={`${getNavLinkClass('/blog')} text-left py-3 px-2 min-h-[44px] touch-manipulation active:bg-muted/50 rounded-lg transition-colors`}
              >
                Blog
              </button>
              {user && (
                <button 
                  onClick={() => handleNavigate('/meu-pin')} 
                  className={`${getNavLinkClass('/meu-pin')} text-left py-3 px-2 min-h-[44px] touch-manipulation active:bg-muted/50 rounded-lg transition-colors`}
                >
                  Meu PIN
                </button>
              )}
              
              {/* Mobile Auth Buttons - Show only after loading completes */}
              {!loading && !adminLoading && (
                <div className="pt-4 mt-2 border-t border-border ios-safe-bottom">
                  {user ? (
                    <div className="flex flex-col space-y-3">
                      {/* Não mostrar créditos para admins */}
                      {!isAdmin && (
                        <div className="mb-2 px-2">
                          <CreditsProgressBar compact showBuyButton={false} />
                        </div>
                      )}
                      
                      {/* Menu do usuário mobile */}
                      <div className="px-2">
                        <UserMenu 
                          onNavigate={handleNavigate} 
                          onLogout={handleLogout} 
                        />
                      </div>
                    </div>
                  ) : (
                    <Button 
                      onClick={() => handleNavigate('/auth')}
                      size="lg"
                      className="w-full justify-start min-h-[44px] touch-manipulation"
                    >
                      <User className="mr-2 h-4 w-4" />
                      Entrar
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;